/**
 * AggregationView
 *
 * Shown to the facilitator after participants have submitted their tool responses.
 * Displays individual responses, computed aggregate (majority/average), and
 * allows per-item facilitator overrides. Records full provenance.
 */

import { useState, useMemo } from 'react';
import {
  Users,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Edit3,
  RotateCcw,
  Gavel,
  TrendingUp,
  BarChart3,
  Info,
  Sparkles,
  X,
} from 'lucide-react';
import { interpretAggregation } from '../../../lib/llmAggregationService';
import { useAiCapability } from '../../../lib/ai/useAiCapability';
import type { AggregationInterpretation } from '../../../lib/llmAggregationService';
import {
  partitionVoiceResponses,
  isVoicePrincipal,
  VoiceBadge,
  VoiceContributionCard,
} from '../../../features/voices';
import { CreationProvenanceBadge } from '../provenance/CreationProvenanceBadge';
import { DivergencePanel } from './DivergencePanel';
import { FairnessFlagPanel } from './FairnessFlagPanel';
import type {
  ToolSession,
  ParticipantResponse,
  AggregateResult,
  FacilitatorOverride,
  ToolResultItem,
  FinalDecision,
} from '../../../types/prioritization/toolSession';
import { useToolSessionStore } from '../../../stores/prioritization/toolSessionStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useDeliberationStore } from '../../../stores/prioritization/deliberationStore';
import { overridesMissingReason } from '../../../lib/deliberationCore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function ConsensusBar({ strength }: { strength: number }) {
  const pct = Math.round(strength * 100);
  const color =
    strength >= 0.8 ? 'bg-[var(--green)]' :
    strength >= 0.5 ? 'bg-[var(--mustard)]' :
    'bg-[var(--coral)]';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-[var(--hover-tint)] rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-[var(--text-ter)] w-8 text-right">{pct}%</span>
    </div>
  );
}

function ParticipantBadge({ name, initials, color, isComplete }: {
  name: string; initials: string; color: string; isComplete: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${!isComplete ? 'opacity-40' : ''}`}
        style={{ backgroundColor: color }}
        title={name}
      >
        {initials}
      </div>
      {!isComplete && <span className="text-xs text-[var(--text-ter)]">Pending</span>}
    </div>
  );
}

// ─── Classification Item Row ──────────────────────────────────────────────────

// Maps internal category codes → human-readable display labels
const CATEGORY_LABELS: Record<string, string> = {
  // Eisenhower Matrix (internal codes → action words)
  ui: 'Do',
  in: 'Schedule',
  un: 'Delegate',
  nn: 'Eliminate',
  // Already-readable forms (passthrough)
  'do': 'Do',
  schedule: 'Schedule',
  delegate: 'Delegate',
  eliminate: 'Eliminate',
  // Impact/Effort and generic
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const QUADRANT_COLORS: Record<string, string> = {
  ui: 'bg-[var(--coral-dim)] text-[var(--coral)]',
  'do': 'bg-[var(--coral-dim)] text-[var(--coral)]',
  in: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  schedule: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  un: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  delegate: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  nn: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
  eliminate: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
  high: 'bg-[var(--coral-dim)] text-[var(--coral)]',
  medium: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  low: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
};

function CategoryPill({ category }: { category: string }) {
  const key = category.toLowerCase();
  const colorClass = QUADRANT_COLORS[key] ?? 'bg-[var(--slate-dim)] text-[var(--text-sec)]';
  const label = CATEGORY_LABELS[key] ?? category;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${colorClass}`}>
      {label}
    </span>
  );
}

interface ClassificationRowProps {
  agg: AggregateResult;
  responses: ParticipantResponse[];
  override?: Partial<FacilitatorOverride>;
  onOverride: (itemId: string, value: string, reason?: string) => void;
  onClearOverride: (itemId: string) => void;
  allCategories: string[];
  isHighlighted?: boolean;
}

function ClassificationRow({ agg, responses, override, onOverride, onClearOverride, allCategories, isHighlighted }: ClassificationRowProps) {
  const [editing, setEditing] = useState(false);
  const [draftCategory, setDraftCategory] = useState('');
  const [draftReason, setDraftReason] = useState('');

  const finalCategory = override?.overrideResult?.category ?? agg.majorityCategory ?? '—';
  const isOverridden = !!override?.overrideResult?.category;

  const handleSave = () => {
    if (draftCategory) {
      onOverride(agg.itemId, draftCategory, draftReason || undefined);
    }
    setEditing(false);
    setDraftCategory('');
    setDraftReason('');
  };

  return (
    <tr
      id={`agg-row-${agg.itemId}`}
      className={`border-b border-[var(--rule)] hover:bg-[var(--surface)] transition-colors ${isOverridden ? 'bg-[var(--mustard-dim)]/30' : ''} ${isHighlighted ? 'ring-2 ring-inset ring-indigo-400 bg-[var(--slate-dim)]/40' : ''}`}
    >
      {/* Item title */}
      <td className="px-4 py-3 text-sm font-medium text-[var(--text-pri)] max-w-[200px] truncate">
        {agg.itemTitle}
      </td>
      {/* Per-participant responses */}
      {responses.map(r => {
        const result = r.results.find(ri => ri.id === agg.itemId);
        return (
          <td key={r.participantId} className="px-3 py-3 text-center">
            {result?.category ? (
              <CategoryPill category={result.category} />
            ) : (
              <span className="text-[var(--text-ter)] text-xs">—</span>
            )}
          </td>
        );
      })}
      {/* Aggregate (majority) */}
      <td className="px-4 py-3">
        <div className="space-y-1">
          {agg.majorityCategory && <CategoryPill category={agg.majorityCategory} />}
          {agg.categoryVotes && (
            <div className="flex flex-wrap gap-1 mt-1">
              {Object.entries(agg.categoryVotes)
                .sort((a, b) => b[1] - a[1])
                .map(([cat, count]) => (
                  <span key={cat} className="text-xs text-[var(--text-ter)]">
                    {CATEGORY_LABELS[cat.toLowerCase()] ?? cat}: {count}
                  </span>
                ))}
            </div>
          )}
          {agg.consensusStrength !== undefined && (
            <ConsensusBar strength={agg.consensusStrength} />
          )}
        </div>
      </td>
      {/* Final / facilitator decision */}
      <td className="px-4 py-3">
        {editing ? (
          <div className="space-y-2 min-w-[180px]">
            <select
              value={draftCategory}
              onChange={e => setDraftCategory(e.target.value)}
              className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full text-xs border rounded px-2 py-1 focus:ring-1 focus:ring-indigo-500"
              autoFocus
            >
              <option value="">Select…</option>
              {allCategories.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Reason (optional)"
              value={draftReason}
              onChange={e => setDraftReason(e.target.value)}
              className="w-full text-xs border rounded px-2 py-1 focus:ring-1 focus:ring-indigo-500"
            />
            <div className="flex gap-1">
              <button onClick={handleSave} className="flex-1 text-xs bg-[var(--slate)] text-white px-2 py-1 rounded hover:bg-[var(--slate)]">Save</button>
              <button onClick={() => setEditing(false)} className="flex-1 text-xs border text-[var(--text-sec)] px-2 py-1 rounded hover:bg-[var(--surface)]">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 group">
            {isOverridden ? (
              <>
                <CategoryPill category={finalCategory} />
                <span className="text-xs text-[var(--mustard)] font-medium">Override</span>
                <button
                  onClick={() => onClearOverride(agg.itemId)}
                  className="opacity-0 group-hover:opacity-100 text-[var(--text-ter)] hover:text-[var(--coral)] ml-1"
                  title="Clear override"
                >
                  <RotateCcw className="h-3 w-3" />
                </button>
              </>
            ) : (
              <CategoryPill category={finalCategory} />
            )}
            <button
              onClick={() => { setEditing(true); setDraftCategory(finalCategory); }}
              className="opacity-0 group-hover:opacity-100 text-[var(--text-ter)] hover:text-[var(--text-ter)]"
              title="Override"
            >
              <Edit3 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

// ─── Ranking Item Row ─────────────────────────────────────────────────────────

interface RankingRowProps {
  agg: AggregateResult;
  responses: ParticipantResponse[];
  override?: Partial<FacilitatorOverride>;
  onOverride: (itemId: string, score: number, reason?: string) => void;
  onClearOverride: (itemId: string) => void;
  rank: number;
  isHighlighted?: boolean;
}

function RankingRow({ agg, responses, override, onOverride, onClearOverride, rank, isHighlighted }: RankingRowProps) {
  const [editing, setEditing] = useState(false);
  const [draftScore, setDraftScore] = useState('');
  const [draftReason, setDraftReason] = useState('');

  const finalScore = override?.overrideResult?.score ?? agg.averageScore ?? 0;
  const isOverridden = override?.overrideResult?.score !== undefined;

  const handleSave = () => {
    const score = parseFloat(draftScore);
    if (!isNaN(score)) {
      onOverride(agg.itemId, score, draftReason || undefined);
    }
    setEditing(false);
    setDraftScore('');
    setDraftReason('');
  };

  const spread = agg.scoreSpread ?? 0;
  const spreadColor = spread < 10 ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : spread < 25 ? 'text-[var(--mustard)]' : 'text-[var(--coral)]';

  return (
    <tr
      id={`agg-row-${agg.itemId}`}
      className={`border-b border-[var(--rule)] hover:bg-[var(--surface)] transition-colors ${isOverridden ? 'bg-[var(--mustard-dim)]/30' : ''} ${isHighlighted ? 'ring-2 ring-inset ring-indigo-400 bg-[var(--slate-dim)]/40' : ''}`}
    >
      {/* Rank + title */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="w-6 h-6 rounded-full bg-[var(--slate-dim)] text-[var(--text-sec)] text-xs font-bold flex items-center justify-center flex-shrink-0">
            {rank}
          </span>
          <span className="text-sm font-medium text-[var(--text-pri)] truncate max-w-[160px]">{agg.itemTitle}</span>
        </div>
      </td>
      {/* Per-participant scores */}
      {responses.map(r => {
        const result = r.results.find(ri => ri.id === agg.itemId);
        return (
          <td key={r.participantId} className="px-3 py-3 text-center text-sm">
            {result?.score !== undefined ? (
              <span className="font-mono font-medium text-[var(--text-sec)]">{result.score.toFixed(1)}</span>
            ) : (
              <span className="text-[var(--text-ter)]">—</span>
            )}
          </td>
        );
      })}
      {/* Aggregate */}
      <td className="px-4 py-3">
        <div className="space-y-1">
          <div className="font-mono font-semibold text-[var(--text-pri)] text-sm">
            {agg.averageScore?.toFixed(1) ?? '—'}
          </div>
          {agg.scoreSpread !== undefined && (
            <div className={`text-xs font-medium ${spreadColor}`}>
              ±{agg.scoreSpread.toFixed(1)} spread
            </div>
          )}
          {agg.scoreRange && (
            <div className="text-xs text-[var(--text-ter)]">
              {agg.scoreRange.min}–{agg.scoreRange.max}
            </div>
          )}
        </div>
      </td>
      {/* Final */}
      <td className="px-4 py-3">
        {editing ? (
          <div className="space-y-2 min-w-[160px]">
            <input
              type="number"
              placeholder="Score"
              value={draftScore}
              onChange={e => setDraftScore(e.target.value)}
              className="w-full text-xs border rounded px-2 py-1 font-mono focus:ring-1 focus:ring-indigo-500"
              autoFocus
            />
            <input
              type="text"
              placeholder="Reason (optional)"
              value={draftReason}
              onChange={e => setDraftReason(e.target.value)}
              className="w-full text-xs border rounded px-2 py-1 focus:ring-1 focus:ring-indigo-500"
            />
            <div className="flex gap-1">
              <button onClick={handleSave} className="flex-1 text-xs bg-[var(--slate)] text-white px-2 py-1 rounded hover:bg-[var(--slate)]">Save</button>
              <button onClick={() => setEditing(false)} className="flex-1 text-xs border text-[var(--text-sec)] px-2 py-1 rounded hover:bg-[var(--surface)]">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 group">
            <span className={`font-mono font-semibold text-sm ${isOverridden ? 'text-[var(--mustard)]' : 'text-[var(--text-pri)]'}`}>
              {finalScore.toFixed(1)}
            </span>
            {isOverridden && <span className="text-xs text-[var(--mustard)] font-medium">Override</span>}
            {isOverridden && (
              <button
                onClick={() => onClearOverride(agg.itemId)}
                className="opacity-0 group-hover:opacity-100 text-[var(--text-ter)] hover:text-[var(--coral)]"
                title="Clear override"
              >
                <RotateCcw className="h-3 w-3" />
              </button>
            )}
            <button
              onClick={() => { setEditing(true); setDraftScore(finalScore.toFixed(1)); }}
              className="opacity-0 group-hover:opacity-100 text-[var(--text-ter)] hover:text-[var(--text-ter)]"
              title="Override"
            >
              <Edit3 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

// ─── Provenance Panel ─────────────────────────────────────────────────────────

function ProvenancePanel({ session, responses, overrides, aggregates }: {
  session: ToolSession;
  responses: ParticipantResponse[];
  overrides: Record<string, Partial<FacilitatorOverride>>;
  aggregates: AggregateResult[];
}) {
  const [expanded, setExpanded] = useState(false);
  const overrideCount = Object.keys(overrides).length;
  const unanimousCount = aggregates.filter(a => (a.consensusStrength ?? 0) >= 0.99).length;
  const contestedCount = aggregates.filter(a => (a.consensusStrength ?? 1) < 0.5).length;
  const withRationale = responses.filter(r => r.rationale && r.rationale.trim().length > 0);

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-[var(--surface)] transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[var(--slate-dim)] flex items-center justify-center">
            <Info className="h-4 w-4 text-[var(--text-ter)]" />
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold text-[var(--text-pri)]">Provenance Record</div>
            <div className="text-xs text-[var(--text-ter)]">
              {responses.length} participants · {unanimousCount} unanimous · {overrideCount} overridden · {contestedCount} contested · {withRationale.length}/{responses.length} explained
            </div>
          </div>
        </div>
        {expanded ? <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" /> : <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />}
      </button>

      {expanded && (
        <div className="border-t border-[var(--rule)] px-5 py-4 space-y-4">
          {/* Stats row */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Participants', value: responses.length, color: 'indigo' },
              { label: 'Unanimous', value: unanimousCount, color: 'green' },
              { label: 'Overridden', value: overrideCount, color: 'amber' },
              { label: 'Contested', value: contestedCount, color: 'red' },
            ].map(({ label, value, color }) => (
              <div key={label} className={`bg-${color}-50  p-3 text-center`}>
                <div className={`text-2xl font-bold text-${color}-700`}>{value}</div>
                <div className={`text-xs text-${color}-600 font-medium`}>{label}</div>
              </div>
            ))}
          </div>

          {/* Override list */}
          {overrideCount > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-2">Facilitator Overrides</h4>
              <div className="space-y-2">
                {Object.entries(overrides).map(([itemId, override]) => {
                  const agg = aggregates.find(a => a.itemId === itemId);
                  if (!agg) return null;
                  const originalValue = session.outputType === 'classification'
                    ? (agg.majorityCategory ? (CATEGORY_LABELS[agg.majorityCategory.toLowerCase()] ?? agg.majorityCategory) : '—')
                    : agg.averageScore?.toFixed(1);
                  const newValue = session.outputType === 'classification'
                    ? (override.overrideResult?.category ? (CATEGORY_LABELS[override.overrideResult.category.toLowerCase()] ?? override.overrideResult.category) : '—')
                    : override.overrideResult?.score?.toFixed(1);
                  return (
                    <div key={itemId} className="flex items-start gap-3 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
                      <AlertTriangle className="h-4 w-4 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[var(--text-pri)]">{agg.itemTitle}</div>
                        <div className="text-xs text-[var(--text-sec)] mt-0.5">
                          Aggregate: <span className="font-mono font-semibold">{originalValue}</span>
                          {' → '}
                          Override: <span className="font-mono font-semibold text-[var(--mustard)]">{newValue}</span>
                        </div>
                        {override.reason && (
                          <div className="text-xs text-[var(--text-ter)] italic mt-1">"{override.reason}"</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Participant timeline */}
          <div>
            <h4 className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-2">Submission Timeline</h4>
            <div className="space-y-2">
              {responses
                .sort((a, b) => a.submittedAt.localeCompare(b.submittedAt))
                .map(r => (
                  <div key={r.participantId} className="text-sm">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0 ${
                          r.principalType === 'voice' ? 'ring-2 ring-electric-mustard-400' : ''
                        }`}
                        style={{ backgroundColor: r.participantColor }}
                      >
                        {r.participantInitials}
                      </div>
                      <span className="font-medium text-[var(--text-pri)]">{r.participantName}</span>
                      {r.principalType === 'voice' && (
                        <span className="inline-flex items-center gap-1.5">
                          <VoiceBadge />
                          <CreationProvenanceBadge
                            source={r.creationSource ?? 'ai'}
                            reviewStatus={r.creationReviewStatus ?? 'none'}
                            provenance={r.creationProvenance}
                            variant="label"
                            compact
                          />
                        </span>
                      )}
                      <span className="text-[var(--text-ter)] text-xs ml-auto">
                        {new Date(r.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <CheckCircle2 className="h-4 w-4 text-[var(--green)] flex-shrink-0" />
                    </div>
                    {r.rationale && r.rationale.trim().length > 0 && (
                      <p className="ml-9 mt-0.5 text-xs text-[var(--text-ter)] italic border-l-2 border-[var(--rule)] pl-2">
                        "{r.rationale.trim()}"
                      </p>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main AggregationView ─────────────────────────────────────────────────────

interface AggregationViewProps {
  toolSessionId: string;
  onFinalize?: (decision: FinalDecision) => void;
  onClose?: () => void;
}

// Re-export FinalDecision for consumers
export type { FinalDecision } from '../../../types/prioritization/toolSession';

export function AggregationView({ toolSessionId, onFinalize, onClose }: AggregationViewProps) {
  const aggregationNarratorEnabled = useAiCapability('aggregation_narrator');
  // Select raw state arrays — never call methods inside selectors, as they return
  // new object references on every snapshot check, causing infinite render loops.
  const toolSessions = useToolSessionStore(s => s.toolSessions);
  const allResponses = useToolSessionStore(s => s.participantResponses);
  const allDecisions = useToolSessionStore(s => s.finalDecisions);
  const computeAggregates = useToolSessionStore(s => s.computeAggregates);
  const finalizeDecision = useToolSessionStore(s => s.finalizeDecision);
  const userId = useUserStore(s => s.userId);
  const [overrideReasonError, setOverrideReasonError] = useState<string | null>(null);

  // Derive session-scoped values via useMemo so references are stable
  const session = useMemo(
    () => toolSessions.find(s => s.id === toolSessionId),
    [toolSessions, toolSessionId]
  );
  const responses = useMemo(
    () => allResponses.filter(r => r.toolSessionId === toolSessionId),
    [allResponses, toolSessionId]
  );
  // Voices Phase 2 (PRD §4): official = humans + counted-snapshot Voice rows;
  // advisory Voice rows stay context-only. Every Voice row is badged.
  const voicePartition = useMemo(() => partitionVoiceResponses(responses), [responses]);
  const officialResponses = voicePartition.official;
  const humanResponses = useMemo(
    () => officialResponses.filter((r) => !isVoicePrincipal(r)),
    [officialResponses],
  );
  const voiceResponses = useMemo(
    () => [...voicePartition.countedVoices, ...voicePartition.advisoryVoices],
    [voicePartition],
  );
  const [showVoiceContributions, setShowVoiceContributions] = useState(false);
  // "With/without agent voice" comparison for the official math (view-only).
  const [includeCountedVoices, setIncludeCountedVoices] = useState(true);
  const existingDecision = useMemo(
    () => allDecisions.find(d => d.toolSessionId === toolSessionId),
    [allDecisions, toolSessionId]
  );

  const [overrides, setOverrides] = useState<Record<string, Partial<FacilitatorOverride>>>({});
  const [highlightedItemId, setHighlightedItemId] = useState<string | null>(null);
  const [finalizing, setFinalizing] = useState(false);
  const [interpretation, setInterpretation] = useState<AggregationInterpretation | null>(null);
  const [isInterpreting, setIsInterpreting] = useState(false);

  const handleInterpret = async () => {
    if (!session) return;
    setIsInterpreting(true);
    setInterpretation(null);
    const result = await interpretAggregation({
      toolName: session.toolName,
      outputType: session.outputType,
      participantCount: completedResponses.length,
      overrideCount: Object.keys(overrides).length,
      items: sortedAggregates.map((agg) => ({
        itemTitle: agg.itemTitle,
        outputType: session.outputType,
        majorityCategory: agg.majorityCategory,
        categoryVotes: agg.categoryVotes,
        consensusStrength: agg.consensusStrength,
        averageScore: agg.averageScore,
        scoreSpread: agg.scoreSpread,
        scoreRange: agg.scoreRange,
      })),
    });
    setInterpretation(result);
    setIsInterpreting(false);
  };

  // Official responses (humans + counted Voices) drive the math and its counts.
  const completedResponses = useMemo(
    () => officialResponses.filter(r => r.isComplete),
    [officialResponses],
  );
  const completedHumanResponses = useMemo(
    () => humanResponses.filter(r => r.isComplete),
    [humanResponses],
  );
  const completedVoiceResponses = useMemo(
    () => voiceResponses.filter((r) => r.isComplete),
    [voiceResponses],
  );
  const completedCountedVoices = useMemo(
    () => voicePartition.countedVoices.filter((r) => r.isComplete),
    [voicePartition],
  );

  // Depend on the responses array, not a count — an in-place revision (same
  // submitted count, changed result content) must still recompute. See
  // docs/3-system/architecture-notes/
  // live-session-participant-view-and-results-visibility.md §6.1.
  const aggregates = useMemo(
    () => computeAggregates(toolSessionId, { excludeCountedVoices: !includeCountedVoices }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toolSessionId, completedResponses, includeCountedVoices]
  );

  const pendingCount = (session?.participantIds.length ?? 0) - completedHumanResponses.length;

  const allCategories = useMemo(() => {
    const cats = new Set<string>();
    responses.forEach(r => r.results.forEach(ri => {
      if (ri.category) cats.add(ri.category);
    }));
    return Array.from(cats).sort();
  }, [responses]);

  // Ranking: sort aggregates by average score descending
  const sortedAggregates = useMemo(() => {
    if (!session) return aggregates;
    if (session.outputType === 'ranking' || session.outputType === 'scoring') {
      return [...aggregates].sort((a, b) => (b.averageScore ?? 0) - (a.averageScore ?? 0));
    }
    return aggregates;
  }, [aggregates, session]);

  const handleOverride = (itemId: string, value: string | number, reason?: string) => {
    const agg = aggregates.find(a => a.itemId === itemId);
    if (!agg) return;
    const overrideResult: ToolResultItem =
      typeof value === 'string'
        ? { id: itemId, name: agg.itemTitle, category: value }
        : { id: itemId, name: agg.itemTitle, score: value };
    setOverrides(prev => ({
      ...prev,
      [itemId]: {
        itemId,
        itemTitle: agg.itemTitle,
        aggregateResult: agg,
        overrideResult,
        reason,
      },
    }));
  };

  // Divergence panel "Resolve" → jump to the item's row in the aggregate table
  // and highlight it so the facilitator can override it in context.
  const handleResolveDivergence = (itemId: string) => {
    setHighlightedItemId(itemId);
    if (typeof document !== 'undefined') {
      const el = document.getElementById(`agg-row-${itemId}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    window.setTimeout(() => {
      setHighlightedItemId(prev => (prev === itemId ? null : prev));
    }, 2400);
  };

  const handleClearOverride = (itemId: string) => {
    setOverrides(prev => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  };

  const handleFinalize = async () => {
    if (!session) return;
    // Doc 21 §6.4 — the override reason is the one mandatory signal input.
    const pendingOverrides = Object.values(overrides).filter(
      (o): o is Omit<FacilitatorOverride, 'overriddenBy' | 'overriddenByName' | 'overriddenAt'> =>
        !!o.itemId && !!o.itemTitle && !!o.overrideResult && !!o.aggregateResult
    );
    const missing = overridesMissingReason(pendingOverrides);
    if (missing.length > 0) {
      setOverrideReasonError(`Give a reason for every override before accepting (${missing.length} missing: ${missing.map((o) => o.itemTitle).join(', ')}).`);
      return;
    }
    setOverrideReasonError(null);
    setFinalizing(true);
    try {
      if (pendingOverrides.length > 0 && session.sessionId && userId) {
        const rec = await useDeliberationStore.getState().recordOverrideJustifications({
          sessionId: session.sessionId,
          workspaceId: session.workspaceId ?? getCurrentWorkspaceId() ?? '',
          actorId: userId,
          overrides: pendingOverrides.map((o) => ({
            itemId: o.itemId,
            aggregateResult: o.aggregateResult,
            overrideResult: o.overrideResult,
            reason: o.reason ?? '',
          })),
        });
        if (!rec.ok) {
          setOverrideReasonError(rec.error ?? 'Could not record the override justification.');
          setFinalizing(false);
          return;
        }
      }
      const decision = finalizeDecision({
        toolSessionId,
        overrides: pendingOverrides,
        decidedBy: userId ?? 'facilitator',
        decidedByName: 'Facilitator',
        // Always finalize from the full official aggregate — the
        // "with/without agent voice" checkbox is a comparison view and must
        // never silently change what gets recorded.
        aggregates: computeAggregates(toolSessionId),
      });
      onFinalize?.(decision);
    } finally {
      setFinalizing(false);
    }
  };

  if (!session) {
    return (
      <div className="flex items-center justify-center h-48 text-[var(--text-ter)]">
        Tool session not found.
      </div>
    );
  }

  const isFinalized = session.status === 'finalized';

  return (
    <div className="flex flex-col h-full bg-[var(--surface)]">
      {/* Header */}
      <div className="bg-[var(--bg-raised)] border-b border-[var(--rule)] px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[var(--slate-dim)] flex items-center justify-center">
                <Users className="h-4 w-4 text-[var(--text-ter)]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[var(--text-pri)]">
                  Aggregation Review — {session.toolName}
                </h2>
                <p className="text-sm text-[var(--text-ter)]">
                  {completedHumanResponses.length} of {session.participantIds.length} participants submitted
                  {pendingCount > 0 && <span className="text-[var(--mustard)]"> · {pendingCount} pending</span>}
                  {completedCountedVoices.length > 0 && (
                    <span className="text-[var(--green)] dark:text-[var(--text-ter)]">
                      {' '}
                      · {completedCountedVoices.length} counted Voice
                      {completedCountedVoices.length === 1 ? '' : 's'} in the aggregate
                    </span>
                  )}
                  {completedVoiceResponses.length - completedCountedVoices.length > 0 && (
                    <span className="text-electric-mustard-700">
                      {' '}
                      · {completedVoiceResponses.length - completedCountedVoices.length} Voice
                      {completedVoiceResponses.length - completedCountedVoices.length === 1 ? '' : 's'} providing context
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {completedCountedVoices.length > 0 && (
              <label
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] border border-[var(--green)] cursor-pointer"
                title="Comparison view only — the recorded aggregate always includes counted Voice input, badged."
              >
                <input
                  type="checkbox"
                  checked={includeCountedVoices}
                  onChange={(e) => setIncludeCountedVoices(e.target.checked)}
                />
                With agent voice
              </label>
            )}
            {completedVoiceResponses.length > 0 && (
              <button
                type="button"
                onClick={() => setShowVoiceContributions((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-electric-mustard-800 bg-electric-mustard-50 border border-electric-mustard-200 hover:bg-electric-mustard-100 transition-colors"
              >
                <VoiceBadge />
                {showVoiceContributions ? 'Hide' : 'Show'} Voice context
              </button>
            )}
            {completedResponses.length > 0 && aggregationNarratorEnabled && (
              <button
                onClick={handleInterpret}
                disabled={isInterpreting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--text-sec)] bg-[var(--slate-dim)] border border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-colors disabled:opacity-60"
                title="AI: Interpret the aggregation patterns"
              >
                <Sparkles className="h-3.5 w-3.5" />
                {isInterpreting ? 'Interpreting…' : 'Interpret Results'}
              </button>
            )}
            {/* Participant avatars */}
            <div className="flex -space-x-2">
              {completedHumanResponses.map(r => (
                <div
                  key={r.participantId}
                  className="w-8 h-8 rounded-full border-2 border-white text-white text-xs font-bold flex items-center justify-center"
                  style={{ backgroundColor: r.participantColor }}
                  title={`${r.participantName} — submitted`}
                >
                  {r.participantInitials}
                </div>
              ))}
              {Array.from({ length: pendingCount }).map((_, i) => (
                <div
                  key={`pending-${i}`}
                  className="w-8 h-8 rounded-full border-2 border-white bg-[var(--hover-tint)] flex items-center justify-center"
                  title="Pending"
                >
                  <Clock className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                </div>
              ))}
            </div>
            {onClose && (
              <button onClick={onClose} className="px-3 py-1.5 text-sm text-[var(--text-sec)] border hover:bg-[var(--surface)]">
                Close
              </button>
            )}
          </div>
        </div>

        {/* Status indicators */}
        <div className="flex items-center gap-4 mt-3">
          <div className="flex items-center gap-1.5 text-xs">
            {session.outputType === 'classification'
              ? <BarChart3 className="h-3.5 w-3.5 text-[var(--text-ter)]" />
              : <TrendingUp className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            }
            <span className="text-[var(--text-sec)] font-medium capitalize">{session.outputType} tool</span>
          </div>
          <div className="text-xs text-[var(--text-ter)]">·</div>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="text-[var(--text-sec)]">{Object.keys(overrides).length} items overridden</span>
          </div>
          {isFinalized && (
            <>
              <div className="text-xs text-[var(--text-ter)]">·</div>
              <div className="flex items-center gap-1.5 text-xs text-[var(--green)] dark:text-[var(--text-ter)] font-medium">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Accepted
              </div>
            </>
          )}
        </div>

        {showVoiceContributions && completedVoiceResponses.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-xs font-medium text-electric-mustard-900 flex items-center gap-2">
              <VoiceBadge />
              {completedCountedVoices.length > 0
                ? 'Voice contributions — counted rows are in the aggregate (badged); advisory rows are context only'
                : 'Voice context (not counted in votes)'}
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {completedVoiceResponses.map((resp) => (
                <VoiceContributionCard key={resp.id} response={resp} compact />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {/* Divergence — worst disagreement first, actionable. Canon 01 §2B. */}
        {completedResponses.length >= 2 && (
          <div className="mb-4">
            <DivergencePanel
              aggregates={aggregates}
              outputType={session.outputType}
              participantCount={completedResponses.length}
              onResolve={handleResolveDivergence}
            />
          </div>
        )}

        {/* Process-fairness concerns raised by participants. Canon 01 §2B (legitimacy). */}
        {session.sessionId && (
          <div className="mb-4">
            <FairnessFlagPanel sessionId={session.sessionId} />
          </div>
        )}

        {/* AI Interpretation Panel */}
        {interpretation && (
          <div className="mb-4 border border-[var(--slate)] bg-[var(--slate-dim)] p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
                <span className="text-sm font-semibold text-[var(--text-sec)]">Aggregation Interpretation</span>
              </div>
              <button
                onClick={() => setInterpretation(null)}
                className="p-0.5 hover:bg-[var(--slate-dim)] rounded"
              >
                <X className="h-3.5 w-3.5 text-[var(--text-ter)]" />
              </button>
            </div>
            <p className="text-sm font-medium text-[var(--text-sec)] mb-1">{interpretation.headline}</p>
            <p className="text-sm text-[var(--text-sec)] mb-3">{interpretation.narrative}</p>
            {interpretation.patterns.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-semibold text-[var(--text-sec)] mb-1 uppercase tracking-wide">Patterns</p>
                <ul className="space-y-0.5">
                  {interpretation.patterns.map((p, i) => (
                    <li key={i} className="text-xs text-[var(--text-sec)] flex items-start gap-1.5">
                      <span className="text-[var(--text-sec)] mt-0.5">•</span>{p}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {interpretation.itemsToWatch.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-semibold text-[var(--text-sec)] mb-1 uppercase tracking-wide">Items to Watch</p>
                <div className="space-y-1.5">
                  {interpretation.itemsToWatch.map((w, i) => (
                    <div key={i} className="flex items-start gap-2 bg-[var(--bg-raised)] border border-[var(--slate)] p-2">
                      <AlertTriangle className="h-3 w-3 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-xs font-medium text-[var(--text-pri)]">{w.title}</span>
                        <span className="text-xs text-[var(--text-sec)]"> — {w.concern}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {interpretation.facilitatorNote && (
              <div className="border-t border-[var(--slate)] pt-2 mt-2">
                <p className="text-xs text-[var(--text-sec)] font-medium">
                  Next step: {interpretation.facilitatorNote}
                </p>
              </div>
            )}
            <p className="text-xs text-[var(--text-sec)] mt-2 text-right">Advisory only — all decisions are yours.</p>
          </div>
        )}

        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden mb-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--rule)] bg-[var(--surface)]">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">
                    Item
                  </th>
                  {completedResponses.map(r => (
                    <th key={r.participantId} className="px-3 py-3 text-center">
                      <ParticipantBadge
                        name={r.participantName}
                        initials={r.participantInitials}
                        color={r.participantColor}
                        isComplete={r.isComplete}
                      />
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">
                    {session.outputType === 'classification' ? 'Majority' : 'Average'}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">
                    Final Decision
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedAggregates.map((agg, i) =>
                  session.outputType === 'classification' ? (
                    <ClassificationRow
                      key={agg.itemId}
                      agg={agg}
                      responses={completedResponses}
                      override={overrides[agg.itemId]}
                      onOverride={handleOverride}
                      onClearOverride={handleClearOverride}
                      allCategories={allCategories}
                      isHighlighted={highlightedItemId === agg.itemId}
                    />
                  ) : (
                    <RankingRow
                      key={agg.itemId}
                      agg={agg}
                      responses={completedResponses}
                      override={overrides[agg.itemId]}
                      onOverride={(id, v) => handleOverride(id, v)}
                      onClearOverride={handleClearOverride}
                      rank={i + 1}
                      isHighlighted={highlightedItemId === agg.itemId}
                    />
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Provenance */}
        <ProvenancePanel
          session={session}
          responses={completedResponses}
          overrides={overrides}
          aggregates={aggregates}
        />
      </div>

      {/* Footer */}
      {!isFinalized && (
        <div className="bg-[var(--bg-raised)] border-t border-[var(--rule)] px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-[var(--text-ter)]">
              {Object.keys(overrides).length === 0
                ? 'Accepting all aggregated results as final.'
                : `${Object.keys(overrides).length} item${Object.keys(overrides).length !== 1 ? 's' : ''} overridden from the aggregate — each needs a reason; it is recorded in the Deliberation Record.`}
              {overrideReasonError && <p className="mt-1 text-xs text-[var(--coral)]">{overrideReasonError}</p>}
            </div>
            <button
              onClick={handleFinalize}
              disabled={finalizing || completedResponses.length === 0}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 shadow-sm"
            >
              <Gavel className="h-4 w-4" />
              {finalizing ? 'Accepting…' : 'Accept result'}
            </button>
          </div>
        </div>
      )}

      {isFinalized && existingDecision && (
        <div className="bg-[var(--green-dim)] border-t border-[var(--green)] px-6 py-4">
          <div className="flex items-center gap-3 text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
            <CheckCircle2 className="h-5 w-5 text-[var(--green)] flex-shrink-0" />
            <span>
              Result accepted by <strong>{existingDecision.decidedByName}</strong> at{' '}
              {new Date(existingDecision.decidedAt).toLocaleString()}.
              {' '}{existingDecision.overrides.length} item{existingDecision.overrides.length !== 1 ? 's were' : ' was'} overridden.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
